import { useState, useEffect, useCallback } from "react";

interface UseCarouselProps {
  itemCount: number;
  autoplayDelay: number;
}

interface UseCarouselReturn {
  activeIndex: number;
  goToNext: () => void;
  goToPrev: () => void;
  goToIndex: (index: number) => void;
}

export const useCarousel = ({ itemCount, autoplayDelay }: UseCarouselProps): UseCarouselReturn => {
  const [activeIndex, setActiveIndex] = useState(0);

  const goToNext = useCallback(() => {
    setActiveIndex((current) => (current + 1) % itemCount);
  }, [itemCount]);

  const goToPrev = useCallback(() => {
    setActiveIndex((current) => (current - 1 + itemCount) % itemCount);
  }, [itemCount]);

  const goToIndex = useCallback(
    (index: number) => {
      if (index >= 0 && index < itemCount) {
        setActiveIndex(index);
      }
    },
    [itemCount],
  );

  // Autoplay functionality
  useEffect(() => {
    if (itemCount <= 1) return;

    const interval = setInterval(() => {
      goToNext();
    }, autoplayDelay);

    return () => clearInterval(interval);
  }, [goToNext, autoplayDelay, itemCount]);

  return {
    activeIndex,
    goToNext,
    goToPrev,
    goToIndex,
  };
};
